import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import SearchSelect from '../../components/SearchSelect';
import { createTool, Tool, ToolStatus, updateTool } from '../../api/toolbox';
import { alertApprovalIfPending, ApiError } from '../../api/request';

// 注册/配置部署工具的 modal.
// 新增 (tool=undefined): 调用 POST /tools
// 编辑 (tool=Tool):     调用 PATCH /tools/:toolKey

interface Props {
    open: boolean;
    tool?: Tool | null;
    onClose: () => void;
    onSaved: () => void;
}

const CATEGORY_OPTS = ['计算', '分析', '图像', '迁移', '输出'].map((c) => ({ value: c, label: c }));
const STATUS_OPTS: { value: ToolStatus; label: string }[] = [
    { value: 'dev', label: '开发中' },
    { value: 'live', label: '已上线' },
];

// schemaToEditorText 落库的 inputSchema JSON → 编辑框文本 (美化缩进; 非 JSON 原样).
function schemaToEditorText(raw: string): string {
    if (!raw || !raw.trim() || raw.trim() === '{}') return '';
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
}

// editorTextToSchemaJSON 编辑框文本 → 落库 JSON. 两种写法都接受:
//   1) JSON 对象 (可嵌套, 值写类型/说明): {"xcode":"str","context":{"thicknessMm":"num, cut 必填"}}
//   2) 逗号分隔字段名 (兼容旧习惯): "xcode, deviceKey" → {"xcode":"str","deviceKey":"str"}
// 返回 {json} 或 {err} (形如 JSON 但解析失败时报错, 防止契约悄悄变成逗号清单).
function editorTextToSchemaJSON(text: string): { json: string; err?: string } {
    const t = text.trim();
    if (!t) return { json: '' };
    if (t.startsWith('{') || t.startsWith('[')) {
        try {
            const obj = JSON.parse(t);
            if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
                return { json: '', err: '输入参数需为 JSON 对象 (不能是数组/标量)' };
            }
            return { json: JSON.stringify(obj) };
        } catch (e) {
            return { json: '', err: `输入参数不是合法 JSON: ${String(e)}` };
        }
    }
    const keys = t
        .split(/[,，\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    if (keys.length === 0) return { json: '' };
    const obj: Record<string, string> = {};
    for (const k of keys) obj[k] = 'str';
    return { json: JSON.stringify(obj) };
}

function ToolRegisterModal({ open, tool, onClose, onSaved }: Props) {
    const isEdit = !!tool;
    const [name, setName] = useState('');
    const [toolKey, setToolKey] = useState('');
    const [category, setCategory] = useState('计算');
    const [status, setStatus] = useState<ToolStatus>('dev');
    const [endpoint, setEndpoint] = useState('');
    const [icon, setIcon] = useState('🛠️');
    const [description, setDescription] = useState('');
    /** 输入参数编辑文本: JSON 对象 (可嵌套) 或逗号分隔字段名, 提交时统一转 JSON 落库. */
    const [inputFields, setInputFields] = useState('');
    const [svcLabel, setSvcLabel] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        if (tool) {
            setName(tool.name);
            setToolKey(tool.toolKey);
            setCategory(tool.category || '计算');
            setStatus(tool.status);
            setEndpoint(tool.endpoint || '');
            setIcon(tool.icon || '🛠️');
            setDescription(tool.description || '');
            setInputFields(schemaToEditorText(tool.inputSchema || ''));
            setSvcLabel(tool.svcLabel || '');
        } else {
            setName('');
            setToolKey('');
            setCategory('计算');
            setStatus('dev');
            setEndpoint('');
            setIcon('🛠️');
            setDescription('');
            setInputFields('');
            setSvcLabel('');
        }
        setError('');
    }, [open, tool]);

    const onSubmit = async () => {
        if (!name.trim() || !toolKey.trim()) {
            setError('工具名称和 key 必填');
            return;
        }
        // 编辑文本 → JSON (后端 MySQL JSON 列要求合法 JSON); 形如 JSON 但非法时阻断提交.
        const { json: inputSchema, err: schemaErr } = editorTextToSchemaJSON(inputFields);
        if (schemaErr) {
            setError(schemaErr);
            return;
        }
        setBusy(true);
        setError('');
        try {
            let message: string;
            if (isEdit && tool) {
                const res = await updateTool(tool.toolKey, {
                    name: name.trim(),
                    category,
                    status,
                    endpoint: endpoint.trim(),
                    icon,
                    description,
                    inputSchema,
                    svcLabel: svcLabel.trim(),
                });
                message = res.message;
            } else {
                const res = await createTool({
                    toolKey: toolKey.trim(),
                    name: name.trim(),
                    category,
                    status,
                    endpoint: endpoint.trim(),
                    icon,
                    description,
                    inputSchema,
                    svcLabel: svcLabel.trim(),
                });
                message = res.message;
            }
            // 普通用户走审批工单: 数据未生效, onSaved 只是回读原列表
            onSaved();
            onClose();
            alertApprovalIfPending(message);
        } catch (e) {
            const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={isEdit ? `配置部署 · ${tool?.name}` : '注册 / 部署新服务'}
        >
            <div className="space-y-4">
                <div className="text-xs text-slate-500">
                    工具以独立服务/插件注册接入, 平台通过统一 Tool API 调用.
                    填写服务信息即可在工具市场注册.
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>工具名称
                        </label>
                        <input
                            className="inp"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="如 参数异常检测"
                        />
                    </div>
                    <div>
                        <label className="field-label">
                            <span className="text-rose-500">* </span>服务标识 (key)
                        </label>
                        <input
                            className={`inp font-mono ${isEdit ? 'bg-slate-50' : ''}`}
                            value={toolKey}
                            onChange={(e) => setToolKey(e.target.value)}
                            placeholder="anomaly-detect"
                            disabled={isEdit}
                            title={isEdit ? 'key 不可改, 删除后重建' : undefined}
                        />
                    </div>
                    <div>
                        <label className="field-label">分类</label>
                        <SearchSelect
                            className="w-full"
                            value={category}
                            onChange={(v) => setCategory(v)}
                            options={CATEGORY_OPTS}
                        />
                    </div>
                    <div>
                        <label className="field-label">状态</label>
                        <SearchSelect
                            className="w-full"
                            value={status}
                            onChange={(v) => setStatus(v as ToolStatus)}
                            options={STATUS_OPTS}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">服务地址 (Endpoint)</label>
                        <input
                            className="inp font-mono"
                            value={endpoint}
                            onChange={(e) => setEndpoint(e.target.value)}
                            placeholder="https://svc.example.com/tools/xxx/invoke"
                        />
                    </div>
                    <div>
                        <label className="field-label">服务标识 (svc)</label>
                        <input
                            className="inp font-mono"
                            value={svcLabel}
                            onChange={(e) => setSvcLabel(e.target.value)}
                            placeholder="engine-svc"
                        />
                    </div>
                    <div>
                        <label className="field-label">图标</label>
                        <input
                            className="inp"
                            value={icon}
                            onChange={(e) => setIcon(e.target.value)}
                            maxLength={4}
                            placeholder="🛠️"
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">描述</label>
                        <input
                            className="inp"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="基于材料 + 设备物理模型求解推荐参数"
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">输入参数 (JSON, 支持嵌套)</label>
                        <textarea
                            className="inp font-mono text-xs"
                            style={{ height: 150, padding: 8, resize: 'vertical' }}
                            value={inputFields}
                            onChange={(e) => setInputFields(e.target.value)}
                            spellCheck={false}
                            placeholder={`{\n  "xcode": "str, 材料编码",\n  "sourceDeviceKey": "str",\n  "sourceParams": { "power": "num, %", "speed": "num, mm/s" },\n  "context": { "thicknessMm": "num, 材料厚度mm (cut 必填)" }\n}`}
                        />
                        <div className="text-[11px] text-slate-400 mt-1">
                            推荐写 JSON 对象 (值填「类型, 说明」, 可嵌套) — 第三方经
                            openapi tools/list·detail 拿到的就是这份结构;
                            也兼容逗号分隔字段名 (类型默认 string). 留空也可.
                        </div>
                    </div>
                </div>
                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}
                <div className="bg-slate-50 rounded p-3 text-xs text-slate-600">
                    注册后: 工具市场出现该卡片 → 灰度验证 → 上线.
                    主后台无需改代码 (插件化接入).
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={onSubmit}
                        disabled={busy}
                    >
                        {busy ? '提交中…' : isEdit ? '保存配置' : '注册服务'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default ToolRegisterModal;
